import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { User } from '../types';
import * as authService from '../services/authService';
import { GOOGLE_CLIENT_ID as CONFIG_GOOGLE_CLIENT_ID } from '../services/configService';

// Fallback client ID in case the environment variable is not available
const GOOGLE_CLIENT_ID = CONFIG_GOOGLE_CLIENT_ID || '375472251002-7jfret9jfkv2touprot3cl1qnhfn7r26.apps.googleusercontent.com';

interface LoginProps {
  onLogin: (username: string) => void;
  onBack?: () => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onBack }) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initializeGoogleAuth = async () => {
      try {
        setLoading(true);
        await authService.initGoogleAuth();

        // Debug: Log the client ID values
        console.log('[DEBUG] Original Google Client ID from config:', CONFIG_GOOGLE_CLIENT_ID);
        console.log('[DEBUG] Final Google Client ID with fallback:', GOOGLE_CLIENT_ID);
        if (!GOOGLE_CLIENT_ID) {
          console.error('[ERROR] Google Client ID is empty or undefined even with fallback');
          setError('Google authentication configuration is missing. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // Check if Google Identity Services is fully loaded
        if (typeof window !== 'undefined' && 
            window.google && 
            typeof window.google === 'object' && 
            window.google.accounts && 
            typeof window.google.accounts === 'object' && 
            window.google.accounts.id && 
            typeof window.google.accounts.id === 'object' &&
            typeof window.google.accounts.id.initialize === 'function' &&
            typeof window.google.accounts.id.renderButton === 'function') {

          try {
            // @ts-ignore - Google Identity Services is loaded via script
            window.google.accounts.id.initialize({
              client_id: GOOGLE_CLIENT_ID,
              callback: handleGoogleResponse,
              auto_select: false,
              cancel_on_tap_outside: true,
            });

            if (googleButtonRef.current) {
              // @ts-ignore - Google Identity Services is loaded via script
              window.google.accounts.id.renderButton(
                googleButtonRef.current,
                { 
                  type: 'standard',
                  theme: 'outline',
                  size: 'large',
                  text: 'signin_with',
                  shape: 'rectangular',
                  logo_alignment: 'left',
                  width: 280,
                  client_id: GOOGLE_CLIENT_ID // Explicitly pass client_id to the button
                }
              );
            } else {
              console.error('Google button reference is not available');
              setError('Failed to render Google sign-in button. Please try again later.');
            }
          } catch (err) {
            console.error('Error initializing Google Identity Services:', err);
            setError('Failed to initialize Google authentication. Please try again later.');
          }
        } else {
          console.error('Google Identity Services not fully loaded');
          setError('Failed to initialize Google authentication. Please try again later.');
        }
      } catch (err) {
        console.error('Error initializing Google Auth:', err);
        setError('Failed to initialize Google authentication. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    initializeGoogleAuth();
  }, []);

  const handleGoogleResponse = (response: any) => {
    // Check if response is valid
    if (!response || !response.credential) {
      console.error('Invalid Google auth response:', response);
      setError('Failed to authenticate with Google. Please try again later.');
      return;
    }

    const user = authService.handleGoogleAuthResponse(response);

    if (user) {
      // Login successful
      onLogin(user.name);
    } else {
      // Login failed - user not authorized
      setError('You are not authorized to access this application. Please contact your administrator.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-20 bg-[url('/hero.png')] bg-cover bg-center"></div>

      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md z-10 relative">
        <div className="absolute top-4 left-4">
             {onBack && (
                 <button onClick={onBack} className="text-gray-400 hover:text-gray-600 flex items-center text-sm transition-colors">
                     <ArrowLeft className="w-4 h-4 mr-1" /> Back to Website
                 </button>
             )}
        </div>

        <div className="text-center mb-8 mt-6">
            <h1 className="text-3xl font-bold text-red-900 mb-2">Herb Brittner's</h1>
            <p className="text-gray-500 font-medium">Order Management System</p>
        </div>

        <div className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="text-center">
            <p className="text-gray-600 mb-4">Sign in with your company Google account</p>
            <div className="flex justify-center">
              <div ref={googleButtonRef}></div>
            </div>
          </div>

          {loading && (
            <div className="text-center text-gray-500">
              <p>Loading authentication...</p>
            </div>
          )}
        </div>

        <p className="mt-8 text-center text-xs text-gray-400">
            Internal Use Only. Authorized Personnel.
        </p>
      </div>
    </div>
  );
};

export default Login;
